import React, {
    PropsWithChildren,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { isAuthorMode, regexValidation } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './ConsumerPrivacyEmailForm.css';
import { Button, Title } from '../../importCL';
import EmailField from '../FieldContainers/EmailField/EmailField';
import {
    InputDropdown,
    InputMasking,
    InputText,
} from '../../component-library';
import useBriteVerify, { IEmailResponse } from '../../hooks/useBriteVerify';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import SessionStorage from '../../utils/SessionStorage';

interface IConsumerPrivacyEmailFormProps extends MappedComponentProperties {
    dropdownLabel?: string;
    headline?: string;
    headlineSize?: string;
    headlineVariant?: string;
    showHorizontalRule?: boolean;
    subHeadline?: string;
    description?: string;
    requestTypeHeading?: string;
    requestTypeOne?: string;
    requestTypeTwo?: string;
    requestTypeThree?: string;
    notEligibleDisclosure?: string;
    submitSuccessText?: string;
    ctaText?: string;
    failedSubmitErrorCode?: string;
    eligibleStates?: string[];
}

export const ConsumerPrivacyEmailFormEditConfig = {
    emptyLabel: 'Consumer Privacy Email Form',
    isEmpty: false, // always shows state field
};

const STATE_OPTIONS = [
    { option: 'Alabama', value: 'AL' },
    { option: 'Alaska', value: 'AK' },
    { option: 'Arizona', value: 'AZ' },
    { option: 'Arkansas', value: 'AR' },
    { option: 'California', value: 'CA' },
    { option: 'Colorado', value: 'CO' },
    { option: 'Connecticut', value: 'CT' },
    { option: 'DC - Washington DC', value: 'DC' },
    { option: 'Delaware', value: 'DE' },
    { option: 'Florida', value: 'FL' },
    { option: 'Georgia', value: 'GA' },
    { option: 'Hawaii', value: 'HI' },
    { option: 'Idaho', value: 'ID' },
    { option: 'Illinois', value: 'IL' },
    { option: 'Indiana', value: 'IN' },
    { option: 'Iowa', value: 'IA' },
    { option: 'Kansas', value: 'KS' },
    { option: 'Kentucky', value: 'KY' },
    { option: 'Louisiana', value: 'LA' },
    { option: 'Maine', value: 'ME' },
    { option: 'Maryland', value: 'MD' },
    { option: 'Massachusetts', value: 'MA' },
    { option: 'Michigan', value: 'MI' },
    { option: 'Minnesota', value: 'MN' },
    { option: 'Mississippi', value: 'MS' },
    { option: 'Missouri', value: 'MO' },
    { option: 'Montana', value: 'MT' },
    { option: 'Nebraska', value: 'NE' },
    { option: 'Nevada', value: 'NV' },
    { option: 'New Hampshire', value: 'NH' },
    { option: 'New Jersey', value: 'NJ' },
    { option: 'New Mexico', value: 'NM' },
    { option: 'New York', value: 'NY' },
    { option: 'North Carolina', value: 'NC' },
    { option: 'North Dakota', value: 'ND' },
    { option: 'Ohio', value: 'OH' },
    { option: 'Oklahoma', value: 'OK' },
    { option: 'Oregon', value: 'OR' },
    { option: 'Pennsylvania', value: 'PA' },
    { option: 'Rhode Island', value: 'RI' },
    { option: 'South Carolina', value: 'SC' },
    { option: 'South Dakota', value: 'SD' },
    { option: 'Tennessee', value: 'TN' },
    { option: 'Texas', value: 'TX' },
    { option: 'Utah', value: 'UT' },
    { option: 'Vermont', value: 'VT' },
    { option: 'Virginia', value: 'VA' },
    { option: 'Washington', value: 'WA' },
    { option: 'West Virginia', value: 'WV' },
    { option: 'Wisconsin', value: 'WI' },
    { option: 'Wyoming', value: 'WY' },
];

const ConsumerPrivacyEmailForm = (
    props: PropsWithChildren<IConsumerPrivacyEmailFormProps>,
): JSX.Element => {
    const stateRef = useRef(null);
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const emailRef = useRef(null);
    const lastFourRef = useRef(null);
    const reqTypeRef = useRef(null);
    const [formData, setFormData] = useState({
        state: { value: '', state: 'neutral' },
        firstName: { value: '', state: 'neutral' },
        lastName: { value: '', state: 'neutral' },
        email: {
            value: '',
            state: 'neutral',
            previousValidation: {
                value: '',
                result: false as false | IEmailResponse,
            },
        },
        lastFourCardDigits: { value: '', state: 'neutral' },
        req: { value: '', state: 'neutral' },
    });
    const [showNotEligibleDisclosure, setShowNotEligibleDisclosure] =
        useState<null | boolean>(null);
    const [isPendingSubmitResult, setIsPendingSubmitResult] = useState(false);
    const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
    const [reqType, setRequestType] = useState('');
    const requestTypes = [
        { option: props.requestTypeOne, value: props.requestTypeOne },
        { option: props.requestTypeTwo, value: props.requestTypeTwo },
        { option: props.requestTypeThree, value: props.requestTypeThree },
    ];
    const { showPushdown } = usePushdownContext();
    const { emailValidationApi } = useBriteVerify();

    const isSelectedStateEligible = () => {
        setShowNotEligibleDisclosure(
            !props.eligibleStates?.includes(stateRef?.current?.value),
        );
    };

    const setValueCallback =
        (value: string) =>
            (
                field:
                | 'state'
                | 'firstName'
                | 'lastName'
                | 'email'
                | 'lastFourCardDigits'
                | 'req'
            ) => {
                setFormData(prev => ({
                    ...prev,
                    [field]: { ...prev[field], value: value },
                }));
            };

    const disableFormSubmitOnEnter = event => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const validateNameField =
        (event: React.FocusEvent<HTMLInputElement>) =>
            (field: 'firstName' | 'lastName') => {
                const value = event.target.value;
                setFormData(prev => ({
                    ...prev,
                    [field]: { ...prev[field], state: value ? 'success' : 'error' },
                }));
            };

    const validateLastFourField = (
        event: React.FocusEvent<HTMLInputElement>,
    ) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            lastFourCardDigits: {
                ...prev.lastFourCardDigits,
                state: !value || value.length === 4 ? 'success' : 'error',
            },
        }));
    };

    const isEmailFormattingInvalid = () => {
        return (
            (formData.email?.value?.match(regexValidation().allow.email) ??
                null) === null
        );
    };

    const validateEmail = async () => {
        const handleSuccess = emailValidationResult => {
            setFormData(prev => ({
                ...prev,
                email: {
                    ...prev.email,
                    state: 'success',
                    previousValidation: emailValidationResult,
                },
            }));
            return true;
        };

        const handleError = () => {
            setFormData(prev => ({
                ...prev,
                email: { ...prev.email, state: 'error' },
            }));
            return false;
        };

        if (isEmailFormattingInvalid()) {
            return handleError();
        }

        if (
            !isAuthorMode() &&
            formData.email.value !== formData.email.previousValidation.value
        ) {
            const result = await emailValidationApi(formData.email.value);
            return !result ? handleError() : handleSuccess(result);
        }

        return handleSuccess(formData.email.previousValidation);
    };

    const submitEmailForm = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsPendingSubmitResult(true);

        if (isAuthorMode()) {
            setIsSubmitSuccess(true);
            return;
        }

        const emailBody = {
            state: formData.state.value,
            firstName: formData.firstName.value,
            lastName: formData.lastName.value,
            email: formData.email.value,
            req: formData.req.value,
        };

        if (formData.lastFourCardDigits.value != '') {
            emailBody.lastFourCardDigits = formData.lastFourCardDigits.value;
        }

        const testEmailParam = SessionStorage.verifyNotEmpty('CCPATestEmail')
            ? `?testEmail=${SessionStorage.get('CCPATestEmail')}`
            : '';
        fetch(`/services/ccpaEmail${testEmailParam}`, {
            method: 'POST',
            body: JSON.stringify(emailBody),
        })
            .then(response => {
                if (response.status !== 200) {
                    setIsPendingSubmitResult(false);
                    showPushdown(props.failedSubmitErrorCode);
                    return;
                }

                SessionStorage.set('CCPA', 'true');
                setIsPendingSubmitResult(false);
                setIsSubmitSuccess(true);
            })
            .catch(() => {
                setIsPendingSubmitResult(false);
                showPushdown(props.failedSubmitErrorCode);
            });
    };

    const isFormValid = useMemo(() => {
        return (
            formData.firstName.state === 'success' &&
            formData.lastName.state === 'success' &&
            formData.email.state === 'success' &&
            formData.lastFourCardDigits.state != 'error'
        );
    }, [
        formData.firstName,
        formData.lastName,
        formData.email,
        formData.lastFourCardDigits,
    ]);

    useEffect(() => {
        if (SessionStorage.verify('CCPA')) {
            setIsSubmitSuccess(true);
        }
    }, []);

    return (
        <form
            className='ConsumerPrivacyEmailForm'
            method='POST'
            onSubmit={submitEmailForm}
            noValidate
        >
            <div className='ConsumerPrivacyEmailForm__heading'>
                <Title
                    tag={props.headlineSize || 'h2'}
                    className='ConsumerPrivacyEmailForm__header'
                    align={props.headlineVariant || 'center'}
                    spanTitle={
                        props.subHeadline && transformHTMLParserLinks(props.subHeadline)
                    }
                    showHr={props.showHorizontalRule}
                >
                    {props.headline}
                </Title>
                {props.description && (
                    <p className='ConsumerPrivacyEmailForm__description'>
                        {transformHTMLParserLinks(props.description)}
                    </p>
                )}
            </div>
            <div>
                <InputDropdown
                    ref={stateRef}
                    id='ConsumerPrivacyEmailForm__state'
                    className='ConsumerPrivacyEmailForm__state'
                    name='State'
                    label={props.dropdownLabel}
                    setValue={value => setValueCallback(value)('state')}
                    onChange={isSelectedStateEligible}
                    options={STATE_OPTIONS}
                    alternate={true}
                    onKeyDown={disableFormSubmitOnEnter}
                    disabled={isSubmitSuccess}
                />
                {showNotEligibleDisclosure && (
                    <div>{transformHTMLParserLinks(props.notEligibleDisclosure)}</div>
                )}
                {isSubmitSuccess && (
                    <div>{transformHTMLParserLinks(props.submitSuccessText)}</div>
                )}
            </div>
            {showNotEligibleDisclosure === false && isSubmitSuccess !== true && (
                <div>
                    <InputText
                        ref={firstNameRef}
                        id='ConsumerPrivacyEmailForm__firstName'
                        name='First Name'
                        label='First Name'
                        state={
                            formData.firstName.state as
                                | 'neutral'
                                | 'success'
                                | 'error'
                        }
                        errorMessage='Please provide a valid First Name'
                        setValue={value => setValueCallback(value)('firstName')}
                        onBlurCallback={event =>
                            validateNameField(event)('firstName')
                        }
                        onKeyDown={disableFormSubmitOnEnter}
                        required
                    />
                    <InputText
                        ref={lastNameRef}
                        id='ConsumerPrivacyEmailForm__lastName'
                        name='Last Name'
                        label='Last Name'
                        state={
                            formData.lastName.state as
                                | 'neutral'
                                | 'success'
                                | 'error'
                        }
                        errorMessage='Please provide a valid Last Name'
                        setValue={value => setValueCallback(value)('lastName')}
                        onBlurCallback={event =>
                            validateNameField(event)('lastName')
                        }
                        onKeyDown={disableFormSubmitOnEnter}
                        required
                    />
                    <EmailField
                        ref={emailRef}
                        name='Email'
                        id='ConsumerPrivacyEmailForm__email'
                        label='Email Address'
                        value={formData.email.value}
                        setValueCallback={value =>
                            setValueCallback(value)('email')
                        }
                        onBlurCallback={async () => {
                            await validateEmail();
                        }}
                        onKeyDown={disableFormSubmitOnEnter}
                        error={formData.email.state === 'error'}
                        errorText='Please provide a valid Email Address: email@domain.com'
                        invalidErrorMessage='Please provide a valid Email Address: email@domain.com'
                        requiredMessage='Please provide a valid Email Address: email@domain.com'
                        showIcon={formData.email.state === 'success'}
                        useSuccessIcon
                        alternate
                    />
                    <InputMasking
                        ref={lastFourRef}
                        allowedChars='0'
                        id='ConsumerPrivacyEmailForm__lastFourCardDigits'
                        name='Last Four Card Digits'
                        label='Last 4 Digits of Card Number'
                        mask='XXXX'
                        maskingOptions=''
                        value={formData.lastFourCardDigits.value}
                        setValue={value =>
                            setValueCallback(value)('lastFourCardDigits')
                        }
                        minLength={4}
                        maxLength={4}
                        autoComplete='off'
                        onKeyDown={disableFormSubmitOnEnter}
                        onBlur={validateLastFourField}
                        error={formData.lastFourCardDigits.state === 'error'}
                        errorText='Please provide the last 4 Digits of your Card Number'
                        invalidErrorMessage='Please provide the last 4 Digits of your Card Number'
                        requiredMessage='Please provide the last 4 Digits of your Card Number'
                        showExclamationError
                        showSuccessIcon={
                            formData.lastFourCardDigits.state === 'success'
                        }
                        useSuccessIcon
                        alternate
                    />
                    <InputDropdown
                        ref={reqTypeRef}
                        id='ConsumerPrivacyEmailForm__requestType'
                        className='ConsumerPrivacyEmailForm__requestType'
                        name='Request Type'
                        errorText='Please select a Request Type'
                        invalidErrorMessage='Please select a Request Type'
                        label={props.requestTypeHeading}
                        setValue={(value) => {
                            setRequestType(value);
                            setValueCallback(value)('req');
                        }}
                        value={reqType}
                        options={requestTypes}
                        alternate={true}
                        onKeyDown={disableFormSubmitOnEnter}
                        disabled={isSubmitSuccess}
                        placeholder='Select a Request Type'
                        showExclamationError
                        required
                    />
                    <Button
                        className='ConsumerPrivacyEmailForm__cta'
                        type='submit'
                        id='ConsumerPrivacyEmailForm__submit'
                        size='medium'
                        variant='solid'
                        disabled={isPendingSubmitResult || !isFormValid}
                    >
                        {transformHTMLParserLinks(props.ctaText)}
                    </Button>
                </div>
            )}
        </form>
    );
};

export default ConsumerPrivacyEmailForm;
